import { useState } from "react";
import { useNavigate } from "react-router";
import { sendEmailVerification, signOut, type User } from "firebase/auth";
import { auth } from "@/data/firebase";
import { useRefreshAuth } from "@/data/AuthProvider";
import { Navbar } from "@/components/layout/Navbar";

/**
 * Shown in place of any protected screen while the signed-in address is
 * unverified.
 *
 * This is a gate, not a page: it has no route of its own, because a route could
 * be navigated away from while the account stayed unverified. `ProtectedRoute`
 * renders it instead of the screen that was asked for, so there is nowhere to
 * go until the address is confirmed.
 *
 * Verification happens in a different tab — usually a different device — so the
 * only way this tab learns about it is by asking. Hence "I've verified it"
 * rather than polling: a poll would burn a token refresh every few seconds for
 * an event that typically arrives minutes later, if at all.
 */
export function VerifyEmail({ user }: { user: User }) {
  const refresh = useRefreshAuth();
  const navigate = useNavigate();

  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stillUnverified, setStillUnverified] = useState(false);

  async function resend() {
    setError(null);
    setSent(false);
    setSending(true);
    try {
      await sendEmailVerification(user);
      setSent(true);
    } catch {
      // Almost always auth/too-many-requests. The address is already on file,
      // so the useful advice is to wait rather than to try something else.
      setError("Could not send another email just yet. Wait a minute and try again.");
    } finally {
      setSending(false);
    }
  }

  async function recheck() {
    setError(null);
    setSent(false);
    setStillUnverified(false);
    setChecking(true);
    try {
      await refresh();
      const current = auth.currentUser;
      if (current?.emailVerified) {
        // The profile write was skipped at sign-up, because the rules reject an
        // unverified write. This is the first moment it can succeed. Imported
        // lazily so the public shell does not carry Firestore.
        const { ensureAppUser } = await import("@/data/users");
        await ensureAppUser(current);
      } else {
        setStillUnverified(true);
      }
    } catch {
      setError("Could not check just now. Try again in a moment.");
    } finally {
      setChecking(false);
    }
  }

  async function leave() {
    await signOut(auth);
    void navigate("/");
  }

  return (
    <div className="flex min-h-screen flex-col">
      <title>Verify your email | Trade-r</title>
      <Navbar />

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <p className="eyebrow">One more step</p>
          <h1 className="mt-1 text-5xl font-black tracking-tighter">Check your email</h1>

          <p className="mt-4 text-ink-soft">
            We sent a verification link to{" "}
            <strong className="font-mono text-ink">{user.email}</strong>. Open it, then come
            back here.
          </p>

          {sent && (
            <p className="notice-info mt-6" role="status">
              Sent again. It can take a minute to arrive — check your spam folder.
            </p>
          )}

          {stillUnverified && (
            <p className="notice-info mt-6" role="status">
              That address still reads as unverified. If you have just clicked the link, give
              it a moment and check again.
            </p>
          )}

          {error && (
            <p className="notice-error mt-6" role="alert">
              {error}
            </p>
          )}

          <div className="mt-8 border-2 border-ink bg-paper-raised p-6">
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => void recheck()}
              disabled={checking || sending}
            >
              {checking ? "Checking…" : "I've verified it"}
            </button>

            <button
              type="button"
              className="btn-outline mt-3 w-full"
              onClick={() => void resend()}
              disabled={sending || checking}
            >
              {sending ? "Sending…" : "Resend the email"}
            </button>
          </div>

          <p className="mt-6 text-center text-sm">
            <button type="button" className="underline" onClick={() => void leave()}>
              Sign out
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
